class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.announcementBar = this.querySelector("#wt-announcement__container");
    this.announcementInterval = null;
    this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    this.changeAnnouncement = this.changeAnnouncement.bind(this);
    this.previousAnnouncement = this.previousAnnouncement.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.hideForOneDay = this.hideForOneDay.bind(this);
    this.restartInterval = this.restartInterval.bind(this);
    this.stopInterval = this.stopInterval.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleMotionChange = this.handleMotionChange.bind(this);
  }

  connectedCallback() {
    if (!this.announcementBar) return;

    this.slides = Array.from(this.announcementBar.children);
    this.isMarquee = this.announcementBar.classList.contains(
      "wt-announcement__container--marquee",
    );

    this.announcementBar.style.transition = "transform 0.5s ease-in-out";
    this.renderCurrentSlide();

    if (!this.isMarquee && this.slides.length > 1) {
      this.announcementBar.addEventListener("touchstart", this.handleTouchStart, {
        passive: true,
      });
      this.announcementBar.addEventListener("touchmove", this.handleTouchMove, {
        passive: true,
      });
      this.announcementBar.addEventListener("touchend", this.handleTouchEnd, {
        passive: true,
      });
      this.addEventListener("mouseenter", this.stopInterval);
      this.addEventListener("mouseleave", this.restartInterval);
      this.addEventListener("focusin", this.stopInterval);
      this.addEventListener("focusout", this.restartInterval);
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
      this.motionQuery.addEventListener?.("change", this.handleMotionChange);
      this.restartInterval();
    }

    this.closeButton = this.querySelector(".wt-announcement__close");
    this.closeButton?.addEventListener("click", this.hideForOneDay);
  }

  restartInterval() {
    this.stopInterval();
    if (
      this.isMarquee ||
      this.slides?.length <= 1 ||
      this.motionQuery.matches ||
      document.hidden
    ) {
      return;
    }
    this.announcementInterval = window.setInterval(this.changeAnnouncement, 5000);
  }

  stopInterval() {
    if (this.announcementInterval) {
      window.clearInterval(this.announcementInterval);
      this.announcementInterval = null;
    }
  }

  handleVisibilityChange() {
    if (document.hidden) this.stopInterval();
    else this.restartInterval();
  }

  handleMotionChange() {
    this.currentIndex = 0;
    this.renderCurrentSlide();
    this.restartInterval();
  }

  hideForOneDay() {
    const oneDayLater = new Date().getTime() + 24 * 60 * 60 * 1000;
    localStorage.setItem("wt-announcement-hidden", oneDayLater);
    this.style.display = "none";
    this.stopInterval();
  }

  changeAnnouncement() {
    if (!this.slides?.length) return;
    this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    this.renderCurrentSlide();
  }

  previousAnnouncement() {
    if (!this.slides?.length) return;
    this.currentIndex =
      (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.renderCurrentSlide();
  }

  renderCurrentSlide() {
    if (!this.announcementBar || !this.slides?.length) return;

    this.announcementBar.style.transform = `translate3d(-${this.currentIndex * 100}%, 0, 0)`;
    this.slides.forEach((slide, index) => {
      const active = index === this.currentIndex;
      slide.setAttribute("aria-hidden", active ? "false" : "true");
      slide.querySelectorAll("a, button").forEach((control) => {
        control.tabIndex = active ? 0 : -1;
      });
    });
  }

  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX;
    this.touchEndX = this.touchStartX;
    this.stopInterval();
  }

  handleTouchMove(event) {
    this.touchEndX = event.touches[0].clientX;
  }

  handleTouchEnd() {
    const swipeDistance = this.touchEndX - this.touchStartX;
    if (Math.abs(swipeDistance) > 30) {
      if (swipeDistance < 0) this.changeAnnouncement();
      else this.previousAnnouncement();
    }
    this.restartInterval();
  }

  disconnectedCallback() {
    this.stopInterval();
    this.announcementBar?.removeEventListener("touchstart", this.handleTouchStart);
    this.announcementBar?.removeEventListener("touchmove", this.handleTouchMove);
    this.announcementBar?.removeEventListener("touchend", this.handleTouchEnd);
    this.removeEventListener("mouseenter", this.stopInterval);
    this.removeEventListener("mouseleave", this.restartInterval);
    this.removeEventListener("focusin", this.stopInterval);
    this.removeEventListener("focusout", this.restartInterval);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.motionQuery.removeEventListener?.("change", this.handleMotionChange);
    this.closeButton?.removeEventListener("click", this.hideForOneDay);
  }
}

if (!customElements.get("announcement-bar")) {
  customElements.define("announcement-bar", AnnouncementBar);
}
